/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Spinner, Alert } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSchool, faSave } from '@fortawesome/free-solid-svg-icons';
import SchoolService from '../../services/school.service';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const SchoolSettings = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cnpj: '',
    phone: ''
  });

  useEffect(() => {
    fetchSchool();
  }, []);

  const fetchSchool = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Busca a primeira escola ativa do usuário
      const activeSchool = user?.school_users?.find(su => su.active);
      if (!activeSchool) {
        setError('Nenhuma escola ativa encontrada.');
        setLoading(false);
        return;
      }

      const data = await SchoolService.getById(activeSchool.school.id);
      setSchool(data);
      setFormData({
        name: data.name || '',
        cnpj: data.cnpj || '',
        phone: data.phone || ''
      });
    } catch (err) {
      setError('Erro ao carregar dados da escola.');
      console.error('Error fetching school:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validação de tipo de arquivo
      if (!file.type.startsWith('image/')) {
        showToast('Por favor, selecione um arquivo de imagem válido.', 'warning');
        return;
      }

      // Validação de tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showToast('O arquivo deve ter no máximo 5MB.', 'warning');
        return;
      }

      setLogoFile(file);

      // Criar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        cnpj: formData.cnpj,
        phone: formData.phone
      };

      // Adiciona logo em base64 se houver arquivo selecionado
      if (logoFile) {
        payload.logo = logoPreview;
      }

      await SchoolService.partialUpdate(school.id, payload);
      await fetchSchool();
      setFormData(prev => ({ ...prev, logo: null }));
      setLogoPreview(null);
      showToast('Dados da escola atualizados com sucesso!', 'success');
    } catch (err) {
      console.error('Error updating school:', err);
      showToast('Erro ao atualizar dados da escola. Tente novamente.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        {error}
      </Alert>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Configurações da Escola</h4>
          <p className="mb-0">Edite as informações da escola</p>
        </div>
      </div>

      <Row>
        <Col xs={12} xl={8} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faSchool} className="me-2" />
                Informações da Escola
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label>Nome da Escola</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Digite o nome da escola"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>CNPJ</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="cnpj"
                        value={formData.cnpj}
                        onChange={handleInputChange}
                        placeholder="00.000.000/0000-00"
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-3">
                    <Form.Group>
                      <Form.Label>Telefone</Form.Label>
                      <Form.Control 
                        type="text" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(00) 0000-0000"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12} className="mb-3">
                    <Form.Group>
                      <Form.Label>Município</Form.Label>
                      <Form.Control 
                        type="text" 
                        value={school?.municipality_name || ''}
                        disabled
                      />
                      <Form.Text className="text-muted">
                        O município não pode ser alterado
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="mt-3">
                  <Button 
                    variant="primary" 
                    type="submit"
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {saving ? 'Salvando...' : 'Salvar Alterações'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} xl={4} className="mb-4">
          <Card border="light" className="shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Logo da Escola</h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center">
                {/* Preview da logo atual ou nova */}
                <div className="mb-3">
                  {logoPreview ? (
                    <div>
                      <p className="text-muted mb-2">
                        <small>Nova logo (não salva ainda)</small>
                      </p>
                      <img 
                        src={logoPreview} 
                        alt="Preview da nova logo" 
                        style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                        className="img-fluid border rounded"
                      />
                    </div>
                  ) : school?.logo ? (
                    <div>
                      <img 
                        src={school.logo} 
                        alt="Logo da escola" 
                        style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
                        className="img-fluid border rounded"
                      />
                    </div>
                  ) : (
                    <div>
                      <FontAwesomeIcon icon={faSchool} size="4x" className="text-secondary" />
                      <p className="text-muted mt-2">
                        <small>Nenhuma logo cadastrada</small>
                      </p>
                    </div>
                  )}
                </div>

                {/* Campo de upload */}
                <div className="mt-3">
                  <Form.Group>
                    <Form.Label className="btn btn-outline-primary btn-sm mb-0">
                      {logoPreview ? 'Alterar Seleção' : 'Selecionar Logo'}
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        style={{ display: 'none' }}
                      />
                    </Form.Label>
                  </Form.Group>

                  {logoPreview && (
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      className="ms-2"
                      onClick={handleRemoveLogo}
                    >
                      Cancelar
                    </Button>
                  )}
                </div>

                <p className="text-muted mt-3 mb-0">
                  <small>Formatos: JPG, PNG, GIF (máx. 5MB)</small>
                </p>
                {logoPreview && (
                  <p className="text-warning mt-2 mb-0">
                    <small>Clique em "Salvar Alterações" para confirmar</small>
                  </p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default SchoolSettings;
